import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ENTRIES, KIND_META } from "./changelogData";

// ─── Constants ────────────────────────────────────────────────────────
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

const KINDS = [
  { value: "all",     label: "Tutte" },
  { value: "feat",    label: "Novità" },
  { value: "fix",     label: "Fix" },
  { value: "ui",      label: "UI" },
  { value: "perf",    label: "Performance" },
  { value: "backend", label: "Backend" },
  { value: "test",    label: "Test" },
];

const MONTHS_IT = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];

// ─── Date helpers ─────────────────────────────────────────────────────
function formatDateIT(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS_IT[m - 1]} ${y}`;
}

function relativeUpdate(iso) {
  const today = new Date();
  const d = new Date(iso);
  const diff = Math.floor((today - d) / MS_PER_DAY);
  if (diff <= 0) return "oggi";
  if (diff === 1) return "ieri";
  if (diff < DAYS_IN_WEEK) return `${diff} giorni fa`;
  if (diff < DAYS_IN_MONTH) return `${Math.floor(diff / DAYS_IN_WEEK)} settimane fa`;
  return formatDateIT(iso);
}

// ─── Inline code renderer ─────────────────────────────────────────────
function renderInlineCode(text, keyPrefix) {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((p, i) => {
    const key = `${keyPrefix}-p${i}`;
    if (p.startsWith("`") && p.endsWith("`")) {
      return (
        <code
          key={key}
          className="px-1 py-0.5 bg-slate-100 border border-slate-200 text-[12px] font-mono text-slate-700 rounded-sm"
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={key}>{p}</React.Fragment>;
  });
}

// ─── Header sub-components ────────────────────────────────────────────
function HeaderBrand() {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <a
        href="/"
        className="flex items-center gap-2.5 text-sm text-slate-700 hover:text-slate-900 transition-colors shrink-0"
        data-testid="changelog-home-link"
      >
        <div className="w-7 h-7 bg-black flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-[10px] tracking-wider">LIB</span>
        </div>
        <span className="font-bold tracking-tight text-slate-900 text-[14px]">
          Incassi Little Italy
        </span>
      </a>
      <nav className="flex items-center">
        <span className="text-slate-300 mx-1.5 text-sm">·</span>
        <a
          href="/"
          className="px-2.5 py-1 text-[13px] text-slate-500 hover:text-slate-900 whitespace-nowrap transition-colors"
          data-testid="tab-app"
        >
          App
        </a>
        <Link
          to="/changelog"
          className="px-2.5 py-1 text-[13px] text-slate-900 font-semibold whitespace-nowrap"
          data-testid="tab-changelog"
        >
          Changelog
        </Link>
      </nav>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  );
}

function HeaderActions() {
  const onShare = () => {
    if (navigator.share) navigator.share({ url: window.location.href }).catch(() => {});
    else navigator.clipboard?.writeText(window.location.href);
  };
  return (
    <button
      onClick={onShare}
      className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 px-2.5 py-1 border border-slate-200 hover:border-slate-300 transition-colors"
      data-testid="share-btn"
      aria-label="Condividi link"
    >
      <ShareIcon />
      <span className="hidden sm:inline">Condividi</span>
    </button>
  );
}

function Header() {
  return (
    <header
      className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-20"
      data-testid="changelog-header"
    >
      <div className="px-[22px] h-14 flex items-center justify-between gap-2">
        <HeaderBrand />
        <HeaderActions />
      </div>
    </header>
  );
}

// ─── Badge / Toc / Filter pills ───────────────────────────────────────
function KindBadge({ kind }) {
  const meta = KIND_META[kind] || KIND_META.feat;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`}
      data-testid={`kind-${kind}`}
    >
      {meta.label}
    </span>
  );
}

function Toc({ entries, activeDate, onSelect }) {
  return (
    <aside className="hidden lg:block">
      <div className="sticky top-24" data-testid="changelog-toc">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">
          Cronologia
        </p>
        <nav className="space-y-0.5 border-l border-slate-200">
          {entries.map((e) => {
            const active = e.date === activeDate;
            return (
              <a
                key={e.date}
                href={`#${e.date}`}
                onClick={() => onSelect(e.date)}
                className={`block pl-3 -ml-px py-1.5 text-xs border-l-2 transition-colors ${
                  active
                    ? "border-slate-900 text-slate-900 font-semibold"
                    : "border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300"
                }`}
                data-testid={`toc-${e.date}`}
              >
                {formatDateIT(e.date)}
              </a>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}

function FilterPills({ kind, setKind }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="kind-filters">
      {KINDS.map((k) => (
        <button
          key={k.value}
          onClick={() => setKind(k.value)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors border ${
            kind === k.value
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
          }`}
          data-testid={`filter-${k.value}`}
        >
          {k.label}
        </button>
      ))}
    </div>
  );
}

// ─── Entry ────────────────────────────────────────────────────────────
function EntryItem({ entry, item }) {
  return (
    <li className="relative" data-testid={`item-${entry.date}-${item.id}`}>
      <span className="absolute -left-[29px] top-1.5 w-1.5 h-1.5 bg-slate-400 rounded-full" />
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <KindBadge kind={item.kind} />
        <strong className="text-slate-900 text-[15px] font-semibold leading-snug">
          {item.title}
        </strong>
      </div>
      <p className="text-[14px] text-slate-600 leading-relaxed">
        {renderInlineCode(item.body, `${entry.date}-${item.id}`)}
      </p>
    </li>
  );
}

function Entry({ entry, isFirst }) {
  return (
    <article
      id={entry.date}
      className="scroll-mt-24 border-t border-slate-200 pt-8 first:border-0 first:pt-0"
      data-testid={`entry-${entry.date}`}
    >
      <div className="flex items-center gap-2 mb-5">
        <span className="w-2.5 h-2.5 rounded-full border-2 border-slate-400" aria-hidden />
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">
          {formatDateIT(entry.date)}
        </h2>
        {isFirst && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-wider">
            Più recente
          </span>
        )}
      </div>
      <ul className="space-y-5 ml-5 border-l border-slate-100 pl-6">
        {entry.items.map((item) => (
          <EntryItem key={`${entry.date}-${item.id}`} entry={entry} item={item} />
        ))}
      </ul>
    </article>
  );
}

// ─── Hero / Controls / List (Changelog sub-components) ────────────────
function ChangelogHero({ totalReleases, totalUpdates, lastUpdate }) {
  return (
    <>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">
        Aggiornamenti prodotto
      </p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-3">
        Cosa c&apos;è di nuovo
      </h1>
      <p className="text-[15px] text-slate-600 mb-6 leading-relaxed">
        Ogni miglioramento, fix e novità di <span className="font-semibold text-slate-900">Incassi Little Italy</span>.
      </p>
      <div className="flex items-center gap-6 text-sm text-slate-600 mb-8 flex-wrap">
        <span data-testid="stat-releases">
          <strong className="text-slate-900 text-base">{totalReleases}</strong>
          <span className="ml-1.5 text-slate-500">release</span>
        </span>
        <span data-testid="stat-updates">
          <strong className="text-slate-900 text-base">{totalUpdates}</strong>
          <span className="ml-1.5 text-slate-500">aggiornamenti</span>
        </span>
        {lastUpdate && (
          <span data-testid="stat-last-update">
            <span className="text-slate-500">Ultimo update </span>
            <strong className="text-slate-900">{relativeUpdate(lastUpdate)}</strong>
          </span>
        )}
      </div>
    </>
  );
}

function SearchIcon() {
  return (
    <svg
      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ChangelogControls({ query, setQuery, kind, setKind }) {
  return (
    <>
      <div className="relative mb-4" data-testid="search-wrapper">
        <SearchIcon />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca tra gli aggiornamenti…"
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 focus:border-slate-400 focus:outline-none placeholder:text-slate-400 transition-colors"
          data-testid="search-input"
        />
      </div>
      <div className="mb-10">
        <FilterPills kind={kind} setKind={setKind} />
      </div>
    </>
  );
}

function ChangelogList({ entries, isFirstHighlightable }) {
  if (entries.length === 0) {
    return (
      <div className="py-20 text-center text-slate-500 text-sm">
        Nessun aggiornamento corrisponde ai filtri.
      </div>
    );
  }
  return (
    <div className="space-y-10">
      {entries.map((entry, i) => (
        <Entry key={entry.date} entry={entry} isFirst={i === 0 && isFirstHighlightable} />
      ))}
    </div>
  );
}

function ChangelogFooter() {
  return (
    <footer className="border-t border-slate-200 py-8 mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-3">
        <span>© Little Italy Barbershop · Gestione Incassi</span>
        <a href="/" className="hover:text-slate-900 transition-colors" data-testid="footer-app-link">
          Torna all&apos;app →
        </a>
      </div>
    </footer>
  );
}

// ─── Hook: filtering + memoized counts ────────────────────────────────
// Note: ENTRIES is a module-scoped constant import, so it's intentionally
// omitted from useMemo deps. React ESLint's exhaustive-deps rule handles
// this correctly for stable module imports.
function useChangelog(kind, query) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ENTRIES.map((entry) => ({
      ...entry,
      items: entry.items.filter((it) => {
        if (kind !== "all" && it.kind !== kind) return false;
        if (q && !(`${it.title} ${it.body}`.toLowerCase().includes(q))) return false;
        return true;
      }),
    })).filter((e) => e.items.length > 0);
  }, [kind, query]);

  const totalUpdates = useMemo(
    () => ENTRIES.reduce((acc, e) => acc + e.items.length, 0),
    []
  );

  return {
    filtered,
    totalUpdates,
    lastUpdate: ENTRIES[0]?.date,
    isFirstHighlightable: kind === "all" && !query,
  };
}

// ─── Main page ────────────────────────────────────────────────────────
export default function Changelog() {
  const [kind, setKind] = useState("all");
  const [query, setQuery] = useState("");
  const [activeDate, setActiveDate] = useState(ENTRIES[0]?.date);
  const { filtered, totalUpdates, lastUpdate, isFirstHighlightable } = useChangelog(kind, query);

  return (
    <div className="min-h-screen bg-white" data-testid="changelog-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid lg:grid-cols-[200px_1fr] gap-10 lg:gap-12 break-words">
        <Toc entries={ENTRIES} activeDate={activeDate} onSelect={setActiveDate} />
        <div>
          <ChangelogHero
            totalReleases={ENTRIES.length}
            totalUpdates={totalUpdates}
            lastUpdate={lastUpdate}
          />
          <ChangelogControls
            query={query} setQuery={setQuery}
            kind={kind} setKind={setKind}
          />
          <ChangelogList entries={filtered} isFirstHighlightable={isFirstHighlightable} />
        </div>
      </main>
      <ChangelogFooter />
    </div>
  );
}
